import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  MenuItem,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import {
  axiosInstance,
  useClientInfo,
  useAppContext,
} from "../utils/commonImports";
import { getFYRange, resolveDateRange } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

const GSTReport = () => {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  // Financial year selected in the header
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // e.g. "2025-26"
  const { start: fyStart, end: fyEnd } = getFYRange(selectedFinancialYear);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [gstReport, setGstReport] = useState("output");
  const [viewType, setViewType] = useState("report");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("none");
  const [reportError, setReportError] = useState("");
  const reportRef = useRef(null);

  const [clientInfo, setClientInfo] = useState(null);

  /* ======================================================
     BACK BUTTON -> always Reports dashboard
  ====================================================== */
  const handleBack = () => navigate(REPORTS_PATH);

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;

      try {
        const res = await axiosInstance.get(`/api/clients/${clientId}`);
        setClientInfo(res.data.client);
      } catch (err) {
        console.error("Client Fetch Error:", err);
      }
    };

    fetchClient();
  }, [clientId]);

  /* ======================================================
     LOAD DATA (FY aware)
  ====================================================== */

  const handleLoad = async ({ ignoreDates = false } = {}) => {
    if (!clientId || financialYearLoading) return;
    try {
      setLoading(true);
      setReportError("");
      let url = "";
      switch (gstReport) {
        case "output": url = "/api/gst/output"; break;
        case "rcm-sales": url = "/api/gst/rcm-sales"; break;
        case "input": url = "/api/gst/input"; break;
        case "rcm-purchase": url = "/api/gst/rcm-purchase"; break;
        case "combined": url = "/api/gst/combined-sales"; break;
        default: url = "/api/gst/output";
      }
      const { from, to } = resolveDateRange(selectedFinancialYear, fromDate, toDate, ignoreDates);
      const res = await axiosInstance.get(url, {
        params: {
          client_id: clientId,
          fromDate: from || null,
          toDate: to || null,
          fyid: selectedFinancialYear?.fyid || "",
          fy_id: selectedFinancialYear?.fyid || "",
          fy,
        },
      });
      const responseData = Array.isArray(res.data) ? res.data
        : Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data?.rows) ? res.data.rows : [];
      setData(responseData);
    } catch (err) {
      console.error("GST Report Load Error:", err.response?.data || err);
      setData([]);
      setReportError(err.response?.data?.message || err.message || "Failed to load GST report.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();
  const handlePdf = () => window.print();


  // Combined GST + RCM statement uses the same invoice row for both tax sections.
  const combinedValue = (row, ...keys) => {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
    }
    return "";
  };

  const combinedNumber = (row, ...keys) => {
    const value = combinedValue(row, ...keys);
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const formatCombinedDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : String(value);
  };

  const renderCombinedTable = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return <Typography>No Records Found</Typography>;
    }

    const columns = [
      ["GSTIN/UIN", (r) => combinedValue(r, "gstin_uin", "customergstin", "gstin", "vendorgstin")],
      ["Customer Name", (r) => combinedValue(r, "customername", "customer_name", "party_name", "vendorname")],
      ["Invoice No", (r) => combinedValue(r, "invoice_number", "invoice_no", "purchase_number", "document_no")],
      ["Invoice Date", (r) => formatCombinedDate(combinedValue(r, "invoice_date", "invoice_dt", "purchase_date"))],
      ["HSN/SAC", (r) => combinedValue(r, "hsn_sac", "hsn", "sac", "hsn_code")],
      ["RCM", (r) => combinedValue(r, "rcm", "is_rcm", "reverse_charge")],
      ["SEZ", (r) => combinedValue(r, "sez", "is_sez")],
      ["Gross Value", (r) => combinedValue(r, "gross_value", "grossvalue")],
      ["Discount", (r) => combinedValue(r, "discount", "discount_amount")],
      ["Taxable Value", (r) => combinedValue(r, "taxable_value", "taxablevalue")],
      ["IGST %", (r) => combinedValue(r, "igst_percent", "igst_rate", "igst_pct")],
      ["IGST", (r) => combinedValue(r, "igst", "igst_amount")],
      ["CGST %", (r) => combinedValue(r, "cgst_percent", "cgst_rate", "cgst_pct")],
      ["CGST", (r) => combinedValue(r, "cgst", "cgst_amount")],
      ["SGST %", (r) => combinedValue(r, "sgst_percent", "sgst_rate", "sgst_pct")],
      ["SGST", (r) => combinedValue(r, "sgst", "sgst_amount")],
      ["Amount After Tax", (r) => combinedValue(r, "amount_after_tax", "amountaftertax", "total")],
      ["RCM-IGST %", (r) => combinedValue(r, "rcm_igst_percent", "rcm_igst_rate", "rcm_igst_pct")],
      ["RCM-IGST", (r) => combinedValue(r, "rcm_igst", "rcm_igst_amount")],
      ["RCM-CGST %", (r) => combinedValue(r, "rcm_cgst_percent", "rcm_cgst_rate", "rcm_cgst_pct")],
      ["RCM-CGST", (r) => combinedValue(r, "rcm_cgst", "rcm_cgst_amount")],
      ["RCM-SGST %", (r) => combinedValue(r, "rcm_sgst_percent", "rcm_sgst_rate", "rcm_sgst_pct")],
      ["RCM-SGST", (r) => combinedValue(r, "rcm_sgst", "rcm_sgst_amount")],
      ["Sales-GST %", (r) => combinedValue(r, "sales_gst_percent", "sales_gst_rate", "sales_gst")],
    ];

    const numericIndexes = new Set([7, 8, 9, 11, 13, 15, 16, 18, 20, 22]);
    const totalFor = (index) => rows.reduce((sum, row) => {
      const value = Number(columns[index][1](row));
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);

    return (
      <TableContainer sx={{ maxHeight: "68vh", overflow: "auto", border: "1px solid #bbb" }}>
        <Table size="small" stickyHeader sx={{
          minWidth: 1900,
          "& th": { border: "1px solid #bbb", fontWeight: 700, fontSize: "0.68rem", backgroundColor: "#eaf1f8", textAlign: "center", whiteSpace: "nowrap" },
          "& td": { border: "1px solid #ddd", fontSize: "0.68rem", padding: "4px 6px", whiteSpace: "nowrap" },
        }}>
          <TableHead>
            <TableRow>
              {columns.map(([label], index) => (
                <TableCell key={label} align={numericIndexes.has(index) ? "right" : "center"}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={`${combinedValue(row, "invoice_number", "invoice_no", "purchase_number") || "row"}-${rowIndex}`}>
                {columns.map(([label, getter], index) => {
                  const value = getter(row);
                  const numeric = numericIndexes.has(index);
                  const parsed = Number(value);
                  const display = value === "" ? "-" :
                    (numeric && Number.isFinite(parsed) ? parsed.toFixed(2) : value);
                  return <TableCell key={label} align={numeric ? "right" : "left"}>{display}</TableCell>;
                })}
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#e5e7eb", fontWeight: 700 }}>
              {columns.map(([label], index) => (
                <TableCell key={label} align={numericIndexes.has(index) ? "right" : "center"}>
                  {index === 0 ? <strong>TOTAL</strong> :
                    numericIndexes.has(index) ? <strong>{totalFor(index).toFixed(2)}</strong> : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleExcel = () => {
    const combinedHeaders = [
      "GSTIN/UIN", "CustomerName", "InvoiceNo", "InvoiceDt", "HSN/SAC", "RCM", "SEZ",
      "GrossValue", "Discount", "TaxableValue", "IGST%", "IGST", "CGST%", "CGST",
      "SGST%", "SGST", "AmountAfterTax", "RCM-IGST%", "RCM-IGST", "RCM-CGST%",
      "RCM-CGST", "RCM-SGST%", "RCM-SGST", "Sales-GST%",
    ];
    const normalHeaders = [
      "Document No", "Date", "Party", "GSTIN", "Place of Supply",
      "CGST %", "SGST %", "IGST %", "Taxable Value",
      "CGST Amount", "SGST Amount", "IGST Amount", "Total",
    ];
    const escapeCsv = (value) => {
      const text = value == null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };
    const csvRows = (Array.isArray(data) ? data : []).map((row) => {
      if (gstReport === "combined") {
        return [
          combinedValue(row, "gstin_uin", "customergstin", "gstin", "vendorgstin"),
          combinedValue(row, "customername", "customer_name", "party_name", "vendorname"),
          combinedValue(row, "invoice_number", "invoice_no", "purchase_number", "document_no"),
          formatCombinedDate(combinedValue(row, "invoice_date", "invoice_dt", "purchase_date")),
          combinedValue(row, "hsn_sac", "hsn", "sac", "hsn_code"),
          combinedValue(row, "rcm", "is_rcm", "reverse_charge"),
          combinedValue(row, "sez", "is_sez"),
          combinedValue(row, "gross_value", "grossvalue"),
          combinedValue(row, "discount", "discount_amount"),
          combinedValue(row, "taxable_value", "taxablevalue"),
          combinedValue(row, "igst_percent", "igst_rate", "igst_pct"),
          combinedValue(row, "igst", "igst_amount"),
          combinedValue(row, "cgst_percent", "cgst_rate", "cgst_pct"),
          combinedValue(row, "cgst", "cgst_amount"),
          combinedValue(row, "sgst_percent", "sgst_rate", "sgst_pct"),
          combinedValue(row, "sgst", "sgst_amount"),
          combinedValue(row, "amount_after_tax", "amountaftertax", "total"),
          combinedValue(row, "rcm_igst_percent", "rcm_igst_rate", "rcm_igst_pct"),
          combinedValue(row, "rcm_igst", "rcm_igst_amount"),
          combinedValue(row, "rcm_cgst_percent", "rcm_cgst_rate", "rcm_cgst_pct"),
          combinedValue(row, "rcm_cgst", "rcm_cgst_amount"),
          combinedValue(row, "rcm_sgst_percent", "rcm_sgst_rate", "rcm_sgst_pct"),
          combinedValue(row, "rcm_sgst", "rcm_sgst_amount"),
          combinedValue(row, "sales_gst_percent", "sales_gst_rate", "sales_gst"),
        ];
      }
      return [
        (row.invoice_number || row.purchase_number || "").trim(),
        row.invoice_date || row.purchase_date ? dayjs(row.invoice_date || row.purchase_date).format("DD/MM/YYYY") : "",
        row.customername || row.party_name || row.vendorname || "",
        row.customergstin || row.gstin || row.vendorgstin || "",
        row.place_of_supply || "",
        row.cgst_percent, row.sgst_percent, row.igst_percent,
        row.taxable_value, row.cgst, row.sgst, row.igst, row.total,
      ];
    });
    const headers = gstReport === "combined" ? combinedHeaders : normalHeaders;
    const csv = "\uFEFF" + [
      headers.map(escapeCsv).join(","),
      ...csvRows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `GST_Report_${gstReport}_${fy || "Report"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const handleScreenshot = async () => {
    if (!reportRef.current) return;
    try {
      setReportError("");
      const module = await import("html2canvas");
      const html2canvas = module.default || module;
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `GST_Report_${gstReport}_${fy || "Report"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("GST screenshot error:", err);
      setReportError('Screenshot requires html2canvas. Run "npm install html2canvas" and try again.');
    }
  };

  // Client or FY changed: clear manual dates and load the whole FY
  useEffect(() => {
    if (!clientId || financialYearLoading) return;
    setFromDate(null);
    setToDate(null);
    handleLoad({ ignoreDates: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading, gstReport]);

  const groupCustomerWise = (rows) => {
    const grouped = {};

    rows.forEach((row) => {
      const key =
        row.customername || row.party_name || row.vendorname || "Unknown";

      if (!grouped[key]) grouped[key] = [];

      grouped[key].push(row);
    });

    return grouped;
  };

  const getReportTitle = () => {
    switch (gstReport) {
      case "output":
        return "GST OUTPUT REPORT (SALES)";
      case "rcm-sales":
        return "GST RCM REPORT (SALES)";
      case "input":
        return "GST INPUT REPORT (PURCHASE)";
      case "rcm-purchase":
        return "GST RCM REPORT (PURCHASE)";
      case "combined":
        return "GST + RCM COMBINED STATEMENT";
      default:
        return "GST REPORT";
    }
  };

  // Range actually applied (picked dates clamped inside the FY)
  const { from: rangeFrom, to: rangeTo } = resolveDateRange(
    selectedFinancialYear,
    fromDate,
    toDate,
  );

  const getDateRange = () => {
    const from = rangeFrom ? dayjs(rangeFrom).format("DD/MM/YYYY") : "All";
    const to = rangeTo ? dayjs(rangeTo).format("DD/MM/YYYY") : "All";

    return `From: ${from}  To: ${to}`;
  };

  /* ======================================================
     TABLE RENDER
  ====================================================== */

  const renderTable = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0)
      return <Typography>No Records Found</Typography>;

    const isGrouped = groupBy === "customer";
    const groupedData = isGrouped ? groupCustomerWise(rows) : null;

    const renderRows = (list) =>
      list.map((row, index) => (
        <TableRow key={index}>
          <TableCell>
            {(row.invoice_number || row.purchase_number)?.trim()}
          </TableCell>

          <TableCell align="center">
            {row.invoice_date || row.purchase_date
              ? dayjs(row.invoice_date || row.purchase_date).format(
                  "DD/MM/YYYY",
                )
              : "-"}
          </TableCell>

          <TableCell>
            {row.customername || row.party_name || row.vendorname || "-"}
          </TableCell>

          <TableCell>
            {row.customergstin || row.gstin || row.vendorgstin || "-"}
          </TableCell>

          <TableCell align="center">{row.place_of_supply || "-"}</TableCell>

          <TableCell align="center">
            {Number(row.cgst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="center">
            {Number(row.sgst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="center">
            {Number(row.igst_percent || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.taxable_value || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.cgst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.sgst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.igst || 0).toFixed(2)}
          </TableCell>

          <TableCell align="right">
            {Number(row.total || 0).toFixed(2)}
          </TableCell>
        </TableRow>
      ));

    return (
      <TableContainer
        sx={{
          maxHeight: "65vh",
          overflow: "auto",
          border: "1px solid #bbb",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 1250,
            "& th": {
              border: "1px solid #bbb",
              fontWeight: 700,
              fontSize: "0.70rem",
              backgroundColor: "#f3f4f6",
              textAlign: "center",
            },
            "& td": {
              border: "1px solid #ddd",
              fontSize: "0.70rem",
              padding: "4px 6px",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80, minWidth: 80 }}>No</TableCell>

              <TableCell sx={{ width: 70, minWidth: 70, textAlign: "center" }}>
                Date
              </TableCell>

              <TableCell sx={{ width: 180, minWidth: 180 }}>Party</TableCell>

              <TableCell sx={{ width: 90, minWidth: 90, textAlign: "center" }}>
                GSTIN
              </TableCell>

              <TableCell sx={{ width: 90, minWidth: 90, textAlign: "center" }}>
                Place of Supply
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                CGST%
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                SGST%
              </TableCell>

              <TableCell sx={{ width: 50, minWidth: 50, textAlign: "left" }}>
                IGST%
              </TableCell>

              <TableCell align="right" sx={{ width: 90, minWidth: 90 }}>
                Taxable
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                CGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                SGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 70, minWidth: 70 }}>
                IGST Amt
              </TableCell>

              <TableCell align="right" sx={{ width: 90, minWidth: 90 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isGrouped
              ? Object.entries(groupedData).map(([customer, list]) => {
                  const subtotal = list.reduce(
                    (a, r) => {
                      a.taxable += Number(r.taxable_value || 0);
                      a.cgst += Number(r.cgst || 0);
                      a.sgst += Number(r.sgst || 0);
                      a.igst += Number(r.igst || 0);
                      a.total += Number(r.total || 0);
                      return a;
                    },
                    { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 },
                  );

                  return (
                    <React.Fragment key={customer}>
                      {/* CUSTOMER HEADER */}
                      <TableRow sx={{ backgroundColor: "#e0f2fe" }}>
                        <TableCell colSpan={13}>
                          <strong>{customer}</strong>
                        </TableCell>
                      </TableRow>

                      {renderRows(list)}

                      {/* CUSTOMER SUBTOTAL */}
                      <TableRow sx={{ backgroundColor: "#fef3c7" }}>
                        <TableCell colSpan={8} align="right">
                          <strong>Customer Total</strong>
                        </TableCell>

                        <TableCell align="right">
                          <strong>{subtotal.taxable.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.cgst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.sgst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.igst.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{subtotal.total.toFixed(2)}</strong>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              : renderRows(rows)}

            {/* GRAND TOTAL */}
            <TableRow sx={{ backgroundColor: "#e5e7eb" }}>
              <TableCell colSpan={8} align="right">
                <strong>Grand Total</strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows
                    .reduce((a, r) => a + Number(r.taxable_value || 0), 0)
                    .toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.cgst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.sgst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows.reduce((a, r) => a + Number(r.igst || 0), 0).toFixed(2)}
                </strong>
              </TableCell>

              <TableCell align="right">
                <strong>
                  {rows
                    .reduce((a, r) => a + Number(r.total || 0), 0)
                    .toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* ======================================================
     JSON VIEW
  ====================================================== */

  const renderJSON = () => (
    <pre
      style={{
        fontSize: 12,
        background: "#111",
        color: "#00ff88",
        padding: 15,
        overflow: "auto",
        maxHeight: "70vh",
      }}
    >
      {JSON.stringify(
        {
          gst_report: gstReport,
          statement: gstReport === "combined" ? "GST + RCM Combined Statement" : undefined,
          report_type: viewType,
          financial_year: fy || null,
          from_date: rangeFrom ? dayjs(rangeFrom).format("DD/MM/YYYY") : null,
          to_date: rangeTo ? dayjs(rangeTo).format("DD/MM/YYYY") : null,
          data: data,
        },
        null,
        2,
      )}
    </pre>
  );

  // limit the pickers to the selected FY
  const pickerLimits = {
    minDate: fyStart ? dayjs(fyStart) : undefined,
    maxDate: fyEnd ? dayjs(fyEnd) : undefined,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={1} sx={{ "@media print": {
        "& .no-print": { display: "none !important" },
        "& .MuiPaper-root": { boxShadow: "none !important" },
        "& .MuiTableContainer-root": { maxHeight: "none !important", overflow: "visible !important" },
        "& table": { minWidth: "0 !important" },
        "& th, & td": { fontSize: "8px !important", padding: "3px !important" },
      } }}>
        <Paper className="no-print" sx={{ p: 1.5, mb: 1.5, border: "1px solid #ccc" }}>
          {/* HEADER WITH BACK BUTTON */}
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                aria-label="Go back"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Box>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  variant="body2"
                  onClick={() => navigate(REPORTS_PATH)}
                >
                  Reports
                </Link>
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                >
                  GST Report
                </Typography>
              </Breadcrumbs>

              <Typography variant="subtitle1" fontWeight={700}>
                GST Report
                {fy && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    FY {fy}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ ml: "auto" }}>
              <Button variant="contained" size="small" startIcon={<SearchIcon />}
                onClick={() => handleLoad()} disabled={loading || financialYearLoading || !clientId}>Search</Button>
              <Button variant="outlined" size="small" startIcon={<PrintIcon />}
                onClick={handlePrint} disabled={loading || data.length === 0}>Print</Button>
              <Button variant="outlined" color="error" size="small" startIcon={<PictureAsPdfIcon />}
                onClick={handlePdf} disabled={loading || data.length === 0}>PDF</Button>
              <Button variant="outlined" color="success" size="small" startIcon={<FileDownloadIcon />}
                onClick={handleExcel} disabled={loading || data.length === 0}>Excel</Button>
              <Button variant="outlined" color="secondary" size="small" startIcon={<CameraAltIcon />}
                onClick={handleScreenshot} disabled={loading || data.length === 0}>Screenshot</Button>
            </Box>
          </Box>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={2.2}>
              <DatePicker
                label="From Date"
                format="DD/MM/YYYY"
                value={fromDate}
                onChange={(v) => setFromDate(v)}
                {...pickerLimits}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2.2}>
              <DatePicker
                label="To Date"
                format="DD/MM/YYYY"
                value={toDate}
                onChange={(v) => setToDate(v)}
                {...pickerLimits}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="GST Reports"
                value={gstReport}
                onChange={(e) => setGstReport(e.target.value)}
              >
                <MenuItem value="output">Output (Sales)</MenuItem>
                <MenuItem value="rcm-sales">RCM – Sales</MenuItem>
                <MenuItem value="input">Input (Purchase)</MenuItem>
                <MenuItem value="rcm-purchase">RCM – Purchase</MenuItem>
                <MenuItem value="combined">GST + RCM (Single Statement)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Report Type"
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
              >
                <MenuItem value="report">Report</MenuItem>
                <MenuItem value="json">JSONS</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Group By"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="customer">Customer Wise</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={1.6}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={() => handleLoad()}
                sx={{
                  height: 40,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {reportError && <Alert severity="error" sx={{ mb: 1 }} className="no-print">{reportError}</Alert>}
        <Paper ref={reportRef} sx={{ p: 1.5, border: "1px solid #ccc" }}>
          {/* REPORT TITLE */}
          <Box
            sx={{
              textAlign: "center",
              mb: 1.5,
              borderBottom: "1px solid #ccc",
              pb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1rem" }}>
              {clientInfo?.name}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem" }}>
              {clientInfo?.address}, {clientInfo?.city}, {clientInfo?.state} -{" "}
              {clientInfo?.zipcode}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem" }}>
              GSTIN: {clientInfo?.gstin} | PAN: {clientInfo?.pan}
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ mt: 1, fontSize: "0.85rem" }}
            >
              {getReportTitle()}
              {fy ? ` (FY ${fy})` : ""}
            </Typography>

            <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              {getDateRange()}
            </Typography>

            <Typography sx={{ fontSize: "0.70rem", mt: 0.5 }}>
              Generated On: {dayjs().format("DD/MM/YYYY hh:mm A")}
            </Typography>
          </Box>

          {loading ? (
            <CircularProgress size={22} />
          ) : viewType === "json" ? (
            renderJSON()
          ) : (
            gstReport === "combined"
              ? renderCombinedTable(Array.isArray(data) ? data : [])
              : renderTable(Array.isArray(data) ? data : [])
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default GSTReport;
